/**
 * Google Fonts CDN utilities
 *
 * Used for dynamically loading fonts that are not in Tweenly's standard font list
 * (e.g. fonts imported from Pencil .pen files).
 */

/**
 * Fonts that are available on most operating systems and don't need CDN loading.
 */
export const SYSTEM_FONTS = new Set([
    "Arial",
    "Book Antiqua",
    "Courier New",
    "Georgia",
    "Helvetica",
    "Impact",
    "Lucida Console",
    "Palatino Linotype",
    "Tahoma",
    "Times New Roman",
    "Verdana",
])

/**
 * Build a Google Fonts CSS2 URL for the given font names.
 * Requests a broad weight range (100–900) to cover most use cases.
 */
export const buildGoogleFontsUrl = (fontNames: string[]): string => {
    if (fontNames.length === 0) return ""
    const families = fontNames
        .map((n) => `family=${n.replace(/ /g, "+")}:wght@100;300;400;500;600;700;800;900`)
        .join("&")
    return `https://fonts.googleapis.com/css2?${families}&display=swap`
}

/**
 * Inject a Google Fonts <link> into document.head for the given font names.
 * Skips fonts already loaded (deduplicates by URL).
 * Safe to call in a browser context only.
 */
/**
 * Build a Google Fonts CSS2 URL requesting only specific weights per font.
 * This avoids downloading unnecessary weight variants.
 */
export const buildGoogleFontsUrlWithWeights = (fontWeights: Map<string, Set<number>>): string => {
    if (fontWeights.size === 0) return ""
    const families = Array.from(fontWeights.entries())
        .map(([name, weights]) => {
            const sortedWeights = Array.from(weights)
                .sort((a, b) => a - b)
                .join(";")
            return `family=${name.replace(/ /g, "+")}:wght@${sortedWeights}`
        })
        .join("&")
    return `https://fonts.googleapis.com/css2?${families}&display=swap`
}

/**
 * Parsed Google Font file entry: original URL and local filename.
 */
export type GoogleFontFile = {
    url: string
    filename: string
    localPath: string
}

/**
 * Fetch the Google Fonts CSS and parse it into @font-face rules with font file URLs.
 * Uses a browser-like User-Agent to get woff2 format.
 * Returns the CSS text and a list of font files to download.
 *
 * When fontWeights is provided, only the specified weights per font are requested.
 * Otherwise falls back to requesting all common weights.
 */
export const fetchGoogleFontsCss = async (
    fontNames: string[],
    fontWeights?: Map<string, Set<number>>
): Promise<{ css: string; fontFiles: GoogleFontFile[] }> => {
    if (fontNames.length === 0) return { css: "", fontFiles: [] }

    const url =
        fontWeights && fontWeights.size > 0
            ? buildGoogleFontsUrlWithWeights(fontWeights)
            : buildGoogleFontsUrl(fontNames)
    const response = await fetch(url, {
        headers: {
            // Request woff2 format (modern browsers)
            "User-Agent":
                "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
        },
    })

    if (!response.ok) {
        console.error(`Failed to fetch Google Fonts CSS: ${response.status}`)
        return { css: "", fontFiles: [] }
    }

    const css = await response.text()
    const fontFiles: GoogleFontFile[] = []
    const usedFilenames = new Set<string>()

    // Split CSS into @font-face blocks and extract metadata for descriptive filenames
    // Google Fonts CSS format: /* subset */ \n @font-face { font-family: '...'; font-weight: ...; ... url(...) ... }
    const blockRegex =
        /\/\*\s*([^*]+?)\s*\*\/\s*@font-face\s*\{[^}]*font-family:\s*'([^']+)'[^}]*font-weight:\s*(\d+)[^}]*url\(([^)]+)\)[^}]*\}/g
    let blockMatch
    while ((blockMatch = blockRegex.exec(css)) !== null) {
        const subset = blockMatch[1].trim()
        const family = blockMatch[2].trim()
        const weight = blockMatch[3]
        const fontUrl = blockMatch[4]

        if (fontUrl.startsWith("http")) {
            const ext = fontUrl.split(".").pop()?.split("?")[0] || "woff2"
            // e.g. "Roboto-400-latin.woff2"
            let filename = `${family.replace(/ /g, "-")}-${weight}-${subset}.${ext}`
            // Handle potential duplicates
            if (usedFilenames.has(filename)) {
                let i = 2
                while (
                    usedFilenames.has(
                        `${family.replace(/ /g, "-")}-${weight}-${subset}-${i}.${ext}`
                    )
                )
                    i++
                filename = `${family.replace(/ /g, "-")}-${weight}-${subset}-${i}.${ext}`
            }
            usedFilenames.add(filename)
            fontFiles.push({
                url: fontUrl,
                filename,
                localPath: `assets/fonts/${filename}`,
            })
        }
    }

    return { css, fontFiles }
}

/**
 * Rewrite Google Fonts CSS to use local file paths instead of CDN URLs.
 */
export const rewriteGoogleFontsCssToLocal = (css: string, fontFiles: GoogleFontFile[]): string => {
    let rewritten = css
    for (const file of fontFiles) {
        rewritten = rewritten.replace(file.url, file.localPath)
    }
    return rewritten
}

export const injectGoogleFonts = (fontNames: string[]): void => {
    if (fontNames.length === 0) return
    const url = buildGoogleFontsUrl(fontNames)
    if (document.querySelector(`link[href="${url}"]`)) return

    if (!document.querySelector('link[rel="preconnect"][href="https://fonts.googleapis.com"]')) {
        const preconnect1 = document.createElement("link")
        preconnect1.rel = "preconnect"
        preconnect1.href = "https://fonts.googleapis.com"
        document.head.appendChild(preconnect1)
    }

    if (!document.querySelector('link[rel="preconnect"][href="https://fonts.gstatic.com"]')) {
        const preconnect2 = document.createElement("link")
        preconnect2.rel = "preconnect"
        preconnect2.href = "https://fonts.gstatic.com"
        preconnect2.crossOrigin = "anonymous"
        document.head.appendChild(preconnect2)
    }

    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = url
    link.crossOrigin = "anonymous"
    link.onerror = () => {
        console.warn(
            `[GoogleFonts] Failed to load fonts: ${fontNames.join(", ")}. ` +
                "Some fonts may not be available on Google Fonts CDN."
        )
    }
    document.head.appendChild(link)
}
